import {
  constant,
  cpiPlus,
  createProject,
  DEFAULT_CATEGORIES,
  DEFAULT_UNCERTAIN_CPI,
  defaultCategoryId,
  TEMPLATES,
  yearMonth,
  type DefaultCategoryKey,
  type InvestmentId,
  type Item,
  type KeyDateId,
  type KeyDateRef,
  type ItemId,
  type MonthIndex,
  type Project,
  type ProjectId,
  type Scenario,
  type ScenarioId,
  type TemplateId,
} from '@finsim/model';

export const PROJECT_TEMPLATES = [
  'blank',
  'rentVsBuy',
  'earlyPayoff',
  'retireEarly',
  'buyToLet',
] as const;
export type ProjectTemplate = (typeof PROJECT_TEMPLATES)[number];

export interface TemplateStrings {
  projectName: string;
  category: (key: DefaultCategoryKey) => string;
  item: (key: TemplateId) => string;
  variant: (
    key:
      'plan' | 'rent' | 'buy' | 'onSchedule' | 'lumpSums' | 'at55' | 'at60' | 'offPlan' | 'deposit',
  ) => string;
  /** Names of template items (and investments) that aren't item templates of their own. */
  extra: (
    key:
      | 'investing'
      | 'homeUpkeep'
      | 'purchaseCosts'
      | 'pension'
      | 'drawdown'
      | 'furnishing'
      | 'runningCosts'
      | 'sellingCosts'
      | 'deposit'
      | 'flat',
  ) => string;
  keyDate: (key: 'completion' | 'sale') => string;
}

export interface TemplateEnv {
  /** Deterministic id factory (injected for tests). */
  id: (prefix: string) => string;
  now: Date;
  currency: string;
  seed: number;
}

const m = (n: number) => n as MonthIndex;

/** Builds a new project from a starting template, with localised names. */
export function buildProject(
  template: ProjectTemplate,
  s: TemplateStrings,
  env: TemplateEnv,
): Project {
  const created = createProject({
    id: env.id('prj') as ProjectId,
    name: s.projectName,
    start: yearMonth(env.now.getUTCFullYear(), env.now.getUTCMonth() + 1),
    currency: env.currency,
    seed: env.seed,
    categoryNames: Object.fromEntries(
      DEFAULT_CATEGORIES.map(([k]) => [k, s.category(k)]),
    ) as Record<DefaultCategoryKey, string>,
  });
  const base: Project = {
    ...created,
    assumptions: { ...created.assumptions, cpi: constant(DEFAULT_UNCERTAIN_CPI) },
  };

  const make = (key: TemplateId, category?: DefaultCategoryKey, start?: number): Item =>
    TEMPLATES[key].create({
      id: env.id('itm') as ItemId,
      name: s.item(key),
      ...(category ? { category: defaultCategoryId(category) } : {}),
      ...(start === undefined ? {} : { start: m(start) }),
    });
  const variant = (
    key: Parameters<TemplateStrings['variant']>[0],
    color: number,
    items: Item[],
  ): Scenario => ({
    id: env.id('var') as ScenarioId,
    name: s.variant(key),
    color,
    itemIds: items.map((i) => i.id),
  });
  const finish = (
    items: Item[],
    scenarios: Scenario[],
    startingCash = 0,
    horizonMonths = 30 * 12,
  ): Project => ({
    ...base,
    assumptions: { ...base.assumptions, startingCash, horizonMonths },
    items: Object.fromEntries(items.map((i) => [i.id, i])),
    scenarios,
    baselineScenarioId: scenarios[0]?.id ?? null,
  });

  if (template === 'blank') {
    const plan = variant('plan', 0, []);
    return { ...base, scenarios: [plan], baselineScenarioId: plan.id };
  }

  const salary = { ...make('salary', 'salary'), amount: { kind: 'constant', value: 3200 } } as Item;
  const tax = make('incomeTax', 'taxes');
  const taxOnSalary: Item =
    tax.type === 'formula'
      ? {
          ...tax,
          spec: { ...tax.spec, sources: [{ ref: { type: 'item', id: salary.id }, sign: 1 }] },
        }
      : tax;
  const living = {
    ...make('livingCosts', 'food'),
    amount: { kind: 'constant', value: 1100 },
  } as Item;

  if (template === 'rentVsBuy') {
    // A fair comparison (docs/PLAN.md M6): renting invests the deposit it keeps and the monthly
    // difference; buying pays purchase costs and upkeep, and the home follows inflation.
    const savings = { ...make('savings'), openingBalance: 15000 } as Item;
    const saving: Item = {
      ...make('subscription'),
      name: s.item('savingsRule'),
      amount: { kind: 'constant', value: 400 },
      growth: { kind: 'indexed', timing: 'annual' },
      account: savings.id,
    } as Item;
    const rent = {
      ...make('rentPaid', 'housing'),
      amount: { kind: 'constant', value: 950 },
    } as Item;
    const investments = {
      ...make('investment'),
      openingBalance: 45000,
      fundedFromCash: true,
    } as Item;
    const investing: Item = {
      ...make('subscription'),
      name: s.extra('investing'),
      amount: constant(200),
      growth: { kind: 'indexed', timing: 'annual' },
      account: investments.id,
    } as Item;
    const home = {
      ...make('property', undefined, 6),
      openingBalance: 220000,
      return: constant(cpiPlus(0)),
    } as Item;
    const mortgage = { ...make('mortgage', 'debt', 6), principal: 180000 } as Item;
    const costs = {
      ...make('purchase', 'housing', 6),
      name: s.extra('purchaseCosts'),
      amount: constant(12000),
      amountBasis: 'atStart',
    } as Item;
    const upkeep = {
      ...make('utilities', 'housing', 6),
      name: s.extra('homeUpkeep'),
      amount: constant(200),
      growth: { kind: 'indexed', timing: 'annual' },
    } as Item;
    const common = [salary, taxOnSalary, living, savings, saving];
    return finish(
      [...common, rent, investments, investing, home, mortgage, costs, upkeep],
      [
        variant('rent', 0, [...common, rent, investments, investing]),
        variant('buy', 1, [...common, home, mortgage, costs, upkeep]),
      ],
      55000,
    );
  }

  if (template === 'buyToLet') {
    // A flat bought off-plan to rent out (docs/ReturnOnInvestment.md): 20 % of €105 000 at
    // signing, the rest by mortgage at completion (month 13), let from month 16, sold after
    // 10 years.
    // Compared with keeping the same money in a deposit; the Returns view is set up.
    const completion = 13;
    const letting = completion + 3;
    const sale = 120;
    // Key dates: moving one (a late building, an earlier sale) moves every date tied to it.
    const keyDates = {
      completion: {
        id: env.id('kd') as KeyDateId,
        name: s.keyDate('completion'),
        month: m(completion),
      },
      sale: { id: env.id('kd') as KeyDateId, name: s.keyDate('sale'), month: m(sale) },
    };
    const tie = (key: keyof typeof keyDates, offset = 0): KeyDateRef => ({
      keyDate: keyDates[key].id,
      offset,
    });
    const once = (name: string, amount: number, start: number, anchor: KeyDateRef): Item =>
      ({
        ...make('purchase', 'housing', start),
        name,
        amount: constant(amount),
        growth: { kind: 'none' },
        anchors: { start: anchor },
      }) as Item;
    // Paid 20 % at signing and 80 % at completion, when the mortgage is paid out; until then the
    // 80 % is owed to the developer. The flat's value grows from signing.
    const flat = {
      ...make('property'),
      openingBalance: 105000,
      payments: [
        { month: m(0), share: 0.2 },
        { month: m(completion), share: 0.8, anchors: { month: tie('completion') } },
      ],
      liquidateAt: m(sale),
      anchors: { liquidateAt: tie('sale') },
      return: {
        kind: 'schedule',
        segments: [
          { from: m(0), value: { kind: 'fixed', rate: 0.08 } },
          {
            from: m(completion),
            value: {
              kind: 'distribution',
              mean: 0.03,
              volatility: 0.06,
              autocorrelation: 0.6,
              floor: -0.15,
              cap: 0.15,
            },
            anchors: { from: tie('completion') },
          },
        ],
      },
    } as Item;
    const mortgage = {
      ...make('mortgage', 'debt', completion),
      principal: 84000,
      termMonths: 360,
      rate: constant({ kind: 'fixed', rate: 0.03 }),
      // Paid into cash, which pays the developer the 80 % in the same month.
      disburseToCash: true,
      // More than the balance left: the sale repays the whole loan.
      earlyRepayments: [
        { month: m(sale), amount: 100000, mode: 'reduceTerm', anchors: { month: tie('sale') } },
      ],
      anchors: { start: tie('completion') },
    } as Item;
    const rent = {
      ...make('rentReceived', 'otherIncome', letting),
      amount: constant(550),
      amountBasis: 'atStart',
      end: m(sale),
      anchors: { start: tie('completion', 3), end: tie('sale') },
    } as Item;
    const running = {
      ...make('utilities', 'housing', completion),
      name: s.extra('runningCosts'),
      amount: constant(83),
      amountBasis: 'atStart',
      end: m(sale),
      anchors: { start: tie('completion'), end: tie('sale') },
    } as Item;
    const deal = [
      flat,
      mortgage,
      once(s.extra('purchaseCosts'), 3700, completion - 1, tie('completion', -1)),
      once(s.extra('furnishing'), 20000, letting, tie('completion', 3)),
      rent,
      running,
      once(s.extra('sellingCosts'), 4400, sale, tie('sale')),
    ];
    const deposit = {
      ...make('savings'),
      name: s.extra('deposit'),
      openingBalance: 46000,
      fundedFromCash: true,
      liquidateAt: m(sale),
      anchors: { liquidateAt: tie('sale') },
      return: constant({ kind: 'fixed', rate: 0.03 }),
    } as Item;
    return {
      ...finish(
        [...deal, deposit],
        [variant('offPlan', 0, deal), variant('deposit', 1, [deposit])],
        46000,
        sale + 12,
      ),
      investments: [{ id: env.id('inv') as InvestmentId, name: s.extra('flat'), scope: 'variant' }],
      keyDates: [keyDates.completion, keyDates.sale],
    };
  }

  if (template === 'retireEarly') {
    // Someone aged 45: stop working at 55 or at 60, live off investments, state pension at 65,
    // plan to 90. The portfolio's returns are uncertain, so Monte Carlo shows the risk.
    const at = (years: number): number => (years - 45) * 12;
    const withEnd = (item: Item, end: number): Item => ({ ...item, end: m(end - 1) }) as Item;
    const withStart = (item: Item, start: number): Item => ({ ...item, start: m(start) });
    const pay = { ...make('salary', 'salary'), amount: { kind: 'constant', value: 3600 } } as Item;
    const incomeTax = make('incomeTax', 'taxes');
    const costs = { ...make('livingCosts', 'food'), amount: constant(1700) } as Item;
    const portfolio = { ...make('investment'), openingBalance: 150000 } as Item;
    const saving = (end: number): Item =>
      withEnd(
        {
          ...make('subscription'),
          name: s.extra('investing'),
          amount: constant(1000),
          growth: { kind: 'indexed', timing: 'annual' },
          account: portfolio.id,
        } as Item,
        end,
      );
    const drawdown = (start: number): Item =>
      withStart(
        {
          ...make('rentReceived'),
          name: s.extra('drawdown'),
          amount: constant(2000),
          account: portfolio.id,
        } as Item,
        start,
      );
    const pension = withStart(
      {
        ...make('rentReceived', 'otherIncome'),
        name: s.extra('pension'),
        amount: constant(900),
      } as Item,
      at(65),
    );
    const payTo55 = withEnd(pay, at(55));
    const payTo60 = withEnd({ ...pay, id: env.id('itm') as ItemId }, at(60));
    const common = [incomeTax, costs, portfolio, pension];
    const early = [payTo55, saving(at(55)), drawdown(at(55))];
    const later = [payTo60, saving(at(60)), drawdown(at(60))];
    return finish(
      [...common, ...early, ...later],
      [variant('at55', 0, [...common, ...early]), variant('at60', 1, [...common, ...later])],
      20000,
      45 * 12,
    );
  }

  // earlyPayoff: the same existing mortgage, paid on schedule vs. with yearly lump sums.
  const loanBase = {
    ...make('mortgage', 'debt'),
    principal: 150000,
    termMonths: 240,
    disburseToCash: false,
  } as Item;
  const lumpSums: Item =
    loanBase.type === 'loan'
      ? {
          ...loanBase,
          id: env.id('itm') as ItemId,
          earlyRepayments: Array.from({ length: 10 }, (_, i) => ({
            month: m(12 * (i + 1)),
            amount: 5000,
            mode: 'reduceTerm' as const,
          })),
        }
      : loanBase;
  const common = [salary, taxOnSalary, living];
  return finish(
    [...common, loanBase, lumpSums],
    [
      variant('onSchedule', 0, [...common, loanBase]),
      variant('lumpSums', 1, [...common, lumpSums]),
    ],
    60000,
  );
}
